import axios from 'axios';
import Bugsnag from '@bugsnag/js';
import _ from 'lodash';

import {
  IS_LOADING,
  AUTH_SIGN_IN,
  AUTH_SIGN_OUT,
  AUTH_REGISTER,
  AUTH_CHANGE_PASSWORD,
  AUTH_FORGOT_PASSWORD,
  AUTH_FORGOT_PASSWORD_VERIFY_OTP,
  AUTH_FORGOT_PASSWORD_CHANGE,
  AUTH_ADD_CONTACT,
  AUTH_ADD_CONTACT_VERIFY,
  AUTH_ADD_CONTACT_UPDATE,
  AUTH_CHANGE_CONTACT_DEFAULT,
  AUTH_CLOST_ACCOUNT,
  AUTH_CLEAR
} from './types';
import {
  LINE_CLIENT_ID,
  LINE_CLIENT_SECRET,
  LINE_REDIRECT_URI,
  SMS_PROVIDER_NAME
} from '../configs';
import { database, firebase } from '../helpers/firebase';

const checkFcm = (docCode, email, isOnline) => {
  const authId = localStorage.getItem('authId');
  const newEmail = email && email !== 'null' ? email : `${docCode}@doctoratoz.co`;
  if (!authId) {
    const firebaseUserOnline = firebase.query(firebase.ref(database, 'users-online'), firebase.orderByChild('email'), firebase.equalTo(newEmail), firebase.limitToLast(1));
    firebase.onChildAdded(firebaseUserOnline, snapshot => {
      if (snapshot?.key) {
        localStorage.setItem('authId', snapshot.key);
        firebase.update(firebase.ref(database, `users-online/${snapshot?.key}`), {
          role: 'doctor',
          status: isOnline ? 'online' : 'offline',
          loginDate: new Date().getTime(),
          updateDate: new Date().getTime()
        });
      }
    });
    setTimeout(() => {
      if (!localStorage.getItem('authId')) {
        firebase.set(firebase.push(firebase.ref(database, 'users-online')), {
          email: newEmail,
          docCode,
          role: 'doctor',
          status: 'offline',
          loginDate: new Date().getTime(),
          updateDate: new Date().getTime()
        });
      }
    }, 1000);
  }
};

export const authSignIn = params => async dispatch => {
  const url = '/accounts/doctorlogin';

  dispatch({ type: AUTH_CLEAR });
  dispatch({
    type: IS_LOADING,
    status: true,
    names: { AUTH_SIGN_IN: true }
  });
  await axios.post(url, params).then(response => {
    let loggedIn = false;
    let accessToken = null;
    let profile = {};
    let message = '';
    let isHasRoleAccess = false;
    let social = null;
    let firstLogin = false;
    if (response?.data?.profiles?.length > 0) {
      social = response?.data?.userRef !== 'null' ? 'social' : null;
      loggedIn = _.has(response.data, 'accessToken');
      accessToken = response?.data?.accessToken || null;
      const data = response.data.profiles.find(item => item.group === params.role);
      if (data && data.profile) {
        profile = data.profile;
        isHasRoleAccess = profile.ekyc || false;
        checkFcm(profile.docCode, profile?.email, profile?.isOnline);
        firstLogin = profile?.firstLogin === true;
        Bugsnag.setUser(profile.docCode, 'developer@doctoratoz.co', 'Bugsnag Web Doctor');
      } else {
        loggedIn = false;
        accessToken = null;
        profile = {};
        isHasRoleAccess = false;
        firstLogin = false;
        message = 'This account is not available. Please contact support';
      }
    } else if (response?.data?.message) {
      message = response.data.message;
    } else {
      message = 'This account is not available. Please contact support';
    }
    const playload = {
      loggedIn,
      accessToken,
      profile,
      social,
      isAccess: false,
      message,
      firstLogin,
      isHasRoleAccess
    };
    dispatch({ type: AUTH_SIGN_IN, playload });
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_SIGN_IN: false } });
  }).catch(err => {
    let error = err;
    const message = 'This account is not available. Please contact support';
    const playload = {
      message
    };
    dispatch({ type: AUTH_SIGN_IN, playload });
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_SIGN_IN: false } });
    if ((_.has(error, 'response.status') && error.response.status === 502) || _.isUndefined(error.response)) {
      if (_.isUndefined(error.response)) { error = Error(`502 API at ${url}`); }
      Bugsnag.notify(error);
    }
  });
};

export const authSignOut = () => dispatch => {
  localStorage.removeItem('authId');
  dispatch({ type: AUTH_SIGN_OUT });
};

export const authClear = () => dispatch => {
  dispatch({ type: AUTH_CLEAR });
};

export const authValidateToken = async token => {
  const url = `/accounts/checkdoctor/${token}`;
  return axios.get(url).then(response => {
    if (_.has(response, 'data.profiles') && _.isArray(response.data.profiles)) {
      const user = response.data.profiles.filter(o => o.group === 'doctor');
      if (user.length > 0 && _.has(user[0], 'profile.docId') && _.has(user[0], 'profile.docCode')) {
        const currentDomain = window.location.hostname;
        const params = {
          userId: user[0].profile.docId,
          userCode: user[0].profile.docCode,
          userRole: 'doctor',
          system_channel: currentDomain,
          domain: currentDomain
        };
        const urlLogging = 'logging/lastLogin';
        axios.post(urlLogging, params);
      }
    }
    return response.data;
  }).catch(err => {
    let error = err;
    if ((_.has(error, 'response.status') && error.response.status === 502) || _.isUndefined(error.response)) {
      if (_.isUndefined(error.response)) { error = Error(`502 API at ${url}`); }
      Bugsnag.notify(error);
    }
    return { error };
  });
};

export const authAccessToken = (data, token) => async dispatch => {
  let profile = {};
  let isHasRoleAccess = false;
  let social = null;
  let message = null;
  let firstLogin = false;
  if (_.has(data, 'profiles') && data.profiles.length > 0) {
    social = data?.social;
    const profiles = data.profiles.find(item => item.group === data.role);
    if (profiles && profiles.profile) {
      message = 'Successful';
      profile = profiles.profile;
      isHasRoleAccess = profile.ekyc || false;
      checkFcm(profile.docCode, profile?.email, profile?.isOnline);
      firstLogin = profile?.firstLogin === true;
      Bugsnag.setUser(profile.docCode, 'developer@doctoratoz.co', 'Bugsnag Web Doctor');
    } else {
      profile = data.profiles[0].profile;
    }
  }
  const playload = {
    loggedIn: true,
    accessToken: token,
    profile,
    social,
    message,
    isAccess: true,
    firstLogin,
    isHasRoleAccess
  };
  dispatch({ type: AUTH_SIGN_IN, playload });
};

export const authRegister = params => async dispatch => {
  const url = '/accounts/register';

  dispatch({ type: AUTH_CLEAR });
  dispatch({ type: IS_LOADING, status: true, names: { AUTH_REGISTER: true } });
  await axios.post(url, params).then(response => {
    const playload = {};
    if (_.has(response.data, 'profiles') && response.data.profiles.length > 0) {
      const data = response.data.profiles.filter(item => item.group === params.role);
      if (data.length > 0) {
        playload.isRegisterSuccess = true;
        playload.profile = data[0].profile;
      }
    } else {
      playload.isRegisterSuccess = false;
      if (_.has(response.data, 'errors') && response.data.errors.length > 0) {
        playload.message = response.data.errors[0].msg;
      } else {
        playload.message = response.data.message;
      }
    }
    playload.status = 'success';
    dispatch({ type: AUTH_REGISTER, playload });
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_REGISTER: false } });
  }).catch(err => {
    let error = err;
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_REGISTER: false } });
    if ((_.has(error, 'response.status') && error.response.status === 502) || _.isUndefined(error.response)) {
      if (_.isUndefined(error.response)) { error = Error(`502 API at ${url}`); }
      Bugsnag.notify(error);
    }
  });
};

export const authChangePassword = (token, oldPassword, newPassword) => async dispatch => {
  const url = '/accounts/changePassword';
  const params = {
    accessToken: token,
    oldPassword,
    newPassword
  };

  dispatch({ type: AUTH_CLEAR });
  dispatch({ type: IS_LOADING, status: true, names: { AUTH_CHANGE_PASSWORD: true } });
  await axios.put(url, params).then(response => {
    const playload = {};
    if (response?.data?.message === 'Success') {
      playload.isChangePasswordSuccess = true;
    } else {
      playload.isChangePasswordSuccess = false;
      if (_.has(response.data, 'errors') && response.data.errors.length > 0) {
        playload.message = response.data.errors[0].msg;
      } else {
        playload.message = response.data.message;
      }
    }
    playload.status = 'success';
    dispatch({ type: AUTH_CHANGE_PASSWORD, playload });
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_CHANGE_PASSWORD: false } });
  }).catch(err => {
    let error = err;
    dispatch({
      type: AUTH_CHANGE_PASSWORD,
      playload: {
        isChangePasswordSuccess: false,
        message: error.response.data.message
      }
    });
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_CHANGE_PASSWORD: false } });
    if ((_.has(error, 'response.status') && error.response.status === 502) || _.isUndefined(error.response)) {
      if (_.isUndefined(error.response)) { error = Error(`502 API at ${url}`); }
      Bugsnag.notify(error);
    }
  });
};

export const authForgotPassword = username => async dispatch => {
  const url = '/accounts/forgotPassword';
  const params = {
    username
  };

  dispatch({ type: AUTH_CLEAR });
  dispatch({ type: IS_LOADING, status: true, names: { AUTH_FORGOT_PASSWORD: true } });
  await axios.put(url, params).then(response => {
    let { message } = response.data;
    if (_.has(response.data, 'errors') && response.data.errors.length > 0) {
      message = response.data.errors[0].msg;
    }
    dispatch({ type: AUTH_FORGOT_PASSWORD, playload: { ref: _.has(response.data, 'data.ref') ? response.data.data.ref : null, msg: message, status: 'success' } });
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_FORGOT_PASSWORD: false } });
  }).catch(err => {
    let error = err;
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_FORGOT_PASSWORD: false } });
    if ((_.has(error, 'response.status') && error.response.status === 502) || _.isUndefined(error.response)) {
      if (_.isUndefined(error.response)) { error = Error(`502 API at ${url}`); }
      Bugsnag.notify(error);
    }
  });
};

export const authForgotPasswordVerifyOTP = (ref, params) => async dispatch => {
  const url = `/accounts/forgotPassword/otp/verify/${ref}`;

  dispatch({ type: AUTH_CLEAR });
  dispatch({ type: IS_LOADING, status: true, names: { AUTH_FORGOT_PASSWORD_VERIFY_OTP: true } });
  await axios.put(url, params).then(response => {
    let verify = true;
    let { message } = response.data;
    if (_.has(response.data, 'errors') && response.data.errors.length > 0) {
      verify = false;
      message = response.data.errors[0].msg;
    } else if (_.has(response.data, 'error')) {
      verify = false;
    }
    dispatch({
      type: AUTH_FORGOT_PASSWORD_VERIFY_OTP,
      playload: {
        ref,
        verify,
        msg: message,
        status: 'success'
      }
    });
    dispatch({
      type: IS_LOADING,
      status: false,
      names: {
        AUTH_FORGOT_PASSWORD_VERIFY_OTP: false
      }
    });
  }).catch(err => {
    let error = err;
    dispatch({
      type: IS_LOADING,
      status: false,
      names: {
        AUTH_FORGOT_PASSWORD_VERIFY_OTP: false
      }
    });
    if ((_.has(error, 'response.status') && error.response.status === 502) || _.isUndefined(error.response)) {
      if (_.isUndefined(error.response)) { error = Error(`502 API at ${url}`); }
      Bugsnag.notify(error);
    }
  });
};

export const authForgotPasswordChange = params => async dispatch => {
  const url = '/accounts/forgotPasswordChange';

  dispatch({ type: AUTH_CLEAR });
  dispatch({ type: IS_LOADING, status: true, names: { AUTH_FORGOT_PASSWORD_CHANGE: true } });
  await axios.put(url, params).then(response => {
    const playload = {};
    if (_.has(response.data, 'user') && response.data.message === 'Success') {
      playload.isChangePasswordSuccess = true;
    } else {
      playload.isChangePasswordSuccess = false;
      if (_.has(response.data, 'errors') && response.data.errors.length > 0) {
        playload.message = response.data.errors[0].msg;
      } else {
        playload.message = response.data.message;
      }
    }
    playload.status = 'success';
    dispatch({ type: AUTH_FORGOT_PASSWORD_CHANGE, playload });
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_FORGOT_PASSWORD_CHANGE: false } });
  }).catch(err => {
    let error = err;
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_FORGOT_PASSWORD_CHANGE: false } });
    if ((_.has(error, 'response.status') && error.response.status === 502) || _.isUndefined(error.response)) {
      if (_.isUndefined(error.response)) { error = Error(`502 API at ${url}`); }
      Bugsnag.notify(error);
    }
  });
};

export const authSignInWithLINE = async code => {
  const url = 'https://api.line.me/oauth2/v2.1/token';
  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    client_id: LINE_CLIENT_ID,
    client_secret: LINE_CLIENT_SECRET,
    redirect_uri: LINE_REDIRECT_URI
  }).toString();
  return axios.post(url, params, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  }).then(response => response.data);
};

export const updateSignal = async (code, signalId) => {
  const url = `/doctors/${code}`;
  return new Promise(reslove => {
    axios.put(url, {
      signalId
    }).then(response => {
      reslove(response.data);
    }).catch(() => {
      reslove({});
    });
  });
};

export const getPatientSignal = async code => {
  const codes = code.split('-');
  let url = `/patients?codes[0]=${code}`;
  if (codes.length > 1) {
    url = `/patients?codes[0]=${codes[0]}`;
  }
  return new Promise(reslove => {
    axios.get(url).then(response => {
      if (_.has(response, 'data.data') && _.isArray(response.data.data) && response.data.data.length > 0 && _.has(response.data.data[0], 'signalId')) {
        reslove(response.data.data[0].signalId);
      } else {
        reslove('');
      }
    }).catch(() => {
      reslove('');
    });
  });
};

export const authAddContect = (docCode, value, type = 'mobile') => async dispatch => {
  const url = type === 'mobile' ? '/accounts/addMobile' : '/accounts/addEmail';
  const params = {
    roleType: 'doctor',
    channel: SMS_PROVIDER_NAME,
    code: docCode
  };
  if (type === 'mobile') {
    params.newMobile = value;
  } else {
    params.newEmail = value;
  }

  dispatch({ type: AUTH_CLEAR });
  dispatch({ type: IS_LOADING, status: true, names: { AUTH_ADD_CONTACT: true } });
  await axios.post(url, params).then(response => {
    const playload = {};
    if (_.has(response.data, 'data.ref')) {
      playload.isAddContact = true;
      playload.authRef = response.data.data.ref;
    } else {
      playload.isAddContact = false;
      if (_.has(response.data, 'errors') && response.data.errors.length > 0) {
        playload.message = response.data.errors[0].msg;
      } else {
        playload.message = response.data.message;
      }
    }
    playload.status = 'success';
    dispatch({ type: AUTH_ADD_CONTACT, playload });
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_ADD_CONTACT: false } });
  }).catch(err => {
    let error = err;
    const playload = {
      status: 'success',
      isAddContact: false
    };
    dispatch({ type: AUTH_ADD_CONTACT, playload });
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_ADD_CONTACT: false } });
    if ((_.has(error, 'response.status') && error.response.status === 502) || _.isUndefined(error.response)) {
      if (_.isUndefined(error.response)) { error = Error(`502 API at ${url}`); }
      Bugsnag.notify(error);
    }
  });
};

export const authAddContactVerify = (refCode, value, OTP, type = 'mobile') => async dispatch => {
  const url = type === 'mobile' ? `/accounts/addMobile/otp/verify/${refCode}` : `/accounts/addEmail/otp/verify/${refCode}`;
  const params = {
    channel: SMS_PROVIDER_NAME,
    otpNumber: OTP
  };
  if (type === 'mobile') {
    params.phone = value;
  } else {
    params.email = value;
  }

  dispatch({ type: AUTH_CLEAR });
  dispatch({ type: IS_LOADING, status: true, names: { AUTH_ADD_CONTACT_VERIFY: true } });
  await axios.put(url, params).then(response => {
    const playload = {
      isAddContactVerify: true,
      message: response.data.message
    };
    if (_.has(response.data, 'errors') && response.data.errors.length > 0) {
      playload.isAddContactVerify = false;
      playload.message = response.data.errors[0].msg;
    } else if (_.has(response.data, 'error')) {
      playload.isAddContactVerify = false;
    }
    playload.status = 'success';
    dispatch({ type: AUTH_ADD_CONTACT_VERIFY, playload });
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_ADD_CONTACT_VERIFY: false } });
  }).catch(err => {
    let error = err;
    const playload = {
      status: 'success',
      isAddContactVerify: false
    };
    dispatch({ type: AUTH_ADD_CONTACT_VERIFY, playload });
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_ADD_CONTACT_VERIFY: false } });
    if ((_.has(error, 'response.status') && error.response.status === 502) || _.isUndefined(error.response)) {
      if (_.isUndefined(error.response)) { error = Error(`502 API at ${url}`); }
      Bugsnag.notify(error);
    }
  });
};

export const authAddContactUpdate = (docCode, value, type = 'mobile') => async dispatch => {
  const url = type === 'mobile' ? '/accounts/addMobileConfirm' : '/accounts/addEmailConfirm';
  const params = {
    roleType: 'doctor',
    channel: SMS_PROVIDER_NAME,
    code: docCode
  };
  if (type === 'mobile') {
    params.mobile = value;
  } else {
    params.email = value;
  }

  dispatch({ type: AUTH_CLEAR });
  dispatch({ type: IS_LOADING, status: true, names: { AUTH_ADD_CONTACT_UPDATE: true } });
  await axios.post(url, params).then(response => {
    const playload = {
      isAddContactUpdate: true,
      message: response.data.message
    };
    if (_.has(response.data, 'errors') && response.data.errors.length > 0) {
      playload.isAddContactUpdate = false;
      playload.message = response.data.errors[0].msg;
    } else if (_.has(response.data, 'error')) {
      playload.isAddContactUpdate = false;
    }
    playload.status = 'success';
    dispatch({ type: AUTH_ADD_CONTACT_UPDATE, playload });
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_ADD_CONTACT_UPDATE: false } });
  }).catch(err => {
    let error = err;
    const playload = {
      status: 'success',
      isAddContactUpdate: false
    };
    dispatch({ type: AUTH_ADD_CONTACT_UPDATE, playload });
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_ADD_CONTACT_UPDATE: false } });
    if ((_.has(error, 'response.status') && error.response.status === 502) || _.isUndefined(error.response)) {
      if (_.isUndefined(error.response)) { error = Error(`502 API at ${url}`); }
      Bugsnag.notify(error);
    }
  });
};

export const authChangeContactDefault = (docCode, value, currentPassword, type = 'mobile') => async dispatch => {
  const url = type === 'mobile' ? '/accounts/changeMainMobile' : '/accounts/changeMainEmail';
  const params = {
    roleType: 'doctor',
    code: docCode,
    currentPassword
  };
  if (type === 'mobile') {
    params.mobile = value;
  } else {
    params.email = value;
  }

  dispatch({ type: AUTH_CLEAR });
  dispatch({ type: IS_LOADING, status: true, names: { AUTH_CHANGE_CONTACT_DEFAULT: true } });
  await axios.post(url, params).then(response => {
    const playload = {
      isChangeContactDefault: true,
      message: response.data.message
    };
    if (_.has(response.data, 'errors') && response.data.errors.length > 0) {
      playload.isChangeContactDefault = false;
      playload.message = response.data.errors[0].msg;
    } else if (_.has(response.data, 'error')) {
      playload.isChangeContactDefault = false;
    }
    if (playload.message === 'Incorrect username or password.') {
      playload.message = 'Incorrect password';
    }
    playload.status = 'success';
    dispatch({ type: AUTH_CHANGE_CONTACT_DEFAULT, playload });
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_CHANGE_CONTACT_DEFAULT: false } });
  }).catch(err => {
    let error = err;
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_CHANGE_CONTACT_DEFAULT: false } });
    if ((_.has(error, 'response.status') && error.response.status === 502) || _.isUndefined(error.response)) {
      if (_.isUndefined(error.response)) { error = Error(`502 API at ${url}`); }
      Bugsnag.notify(error);
    }
  });
};

export const authCloseAccount = (docCode, reason, currentPassword) => async dispatch => {
  const url = '/accounts/close';
  const params = {
    roleType: 'doctor',
    code: docCode,
    reason,
    currentPassword
  };

  dispatch({ type: AUTH_CLEAR });
  dispatch({ type: IS_LOADING, status: true, names: { AUTH_CLOST_ACCOUNT: true } });
  await axios.post(url, params).then(response => {
    const playload = {
      isCloseAccount: true,
      message: response.data.message
    };
    if (_.has(response.data, 'errors') && response.data.errors.length > 0) {
      playload.isCloseAccount = false;
      playload.message = response.data.errors[0].msg;
    } else if (_.has(response.data, 'error')) {
      playload.isCloseAccount = false;
    }
    if (playload.message === 'Incorrect username or password.') {
      playload.message = 'Incorrect password';
    }
    playload.status = 'success';
    dispatch({ type: AUTH_CLOST_ACCOUNT, playload });
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_CLOST_ACCOUNT: false } });
  }).catch(err => {
    let error = err;
    dispatch({ type: IS_LOADING, status: false, names: { AUTH_CLOST_ACCOUNT: false } });
    if ((_.has(error, 'response.status') && error.response.status === 502) || _.isUndefined(error.response)) {
      if (_.isUndefined(error.response)) { error = Error(`502 API at ${url}`); }
      Bugsnag.notify(error);
    }
  });
};

export const getCompanyByCode = async companyCode => {
  const url = `/companies?code=${companyCode}`;
  return new Promise(reslove => {
    axios.get(url).then(response => {
      reslove(response.data);
    }).catch(() => {
      reslove('');
    });
  });
};
